import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import {
  ArrowLeft, Users, BookOpen, BarChart3, Download, Plus,
  Upload, Loader2, X, Check, AlertTriangle, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const inp  = "w-full bg-[#0E0F12] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#CFFF50]/40 placeholder-gray-600";
const lbl  = "block text-xs text-gray-400 mb-1.5 font-medium";
const API  = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_STYLE = {
  completed:   "bg-lime/10 text-lime",
  in_progress: "bg-[#7C5CFF]/10 text-[#7C5CFF]",
  not_started: "bg-white/5 text-gray-500",
  overdue:     "bg-red-500/10 text-red-400",
};

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" }); } catch { return d; } };

export default function AdminOrganisationDetail() {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [org,      setOrg]     = useState(null);
  const [courses,  setCourses] = useState([]);
  const [loading,  setLoading] = useState(true);
  const [tab,      setTab]     = useState("overview");
  const [report,   setReport]  = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [showImport,    setShowImport]    = useState(false);
  const [showAssign,    setShowAssign]    = useState(false);

  const load = () =>
    Promise.all([api.get(`/admin/organisations/${orgId}`), api.get("/admin/courses")])
      .then(([o, cs]) => { setOrg(o); setCourses(cs); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, [orgId]); // eslint-disable-line

  const loadReport = async () => {
    if (report) return;
    setReportLoading(true);
    try { setReport(await api.get(`/admin/organisations/${orgId}/report`)); }
    catch { toast.error("Failed to load report"); }
    finally { setReportLoading(false); }
  };

  useEffect(() => { if (tab === "report") loadReport(); }, [tab]); // eslint-disable-line

  const exportCsv = () => { window.open(`${API}/api/admin/organisations/${orgId}/report.csv`, "_blank"); };

  if (loading) return (
    <AppLayout><div className="flex items-center justify-center py-24 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div></AppLayout>
  );
  if (!org) return (
    <AppLayout><div className="text-center py-20"><p className="text-gray-400">Organisation not found</p></div></AppLayout>
  );

  const pct = org.seat_limit ? Math.round((org.seats_used / org.seat_limit) * 100) : 0;
  const assignedIds = new Set((org.assigned_courses || []).map((ac) => (typeof ac === "string" ? ac : ac.course_id)));

  return (
    <AppLayout searchPlaceholder="Search...">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/admin/organisations")} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
          <ArrowLeft size={15} /> Organisations
        </button>
        <span className="text-gray-700">/</span>
        <h1 className="font-black text-2xl tracking-tight">{org.name}</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Seats used",    value: `${org.seats_used} / ${org.seat_limit}` },
          { label: "Learners",      value: (org.learners || []).length },
          { label: "Courses",       value: (org.assigned_courses || []).length },
          { label: "Avg completion",value: org.courses_detail?.length
              ? `${Math.round(org.courses_detail.reduce((a, c) => a + c.avg_progress, 0) / org.courses_detail.length)}%`
              : "—" },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
            <div className="text-2xl font-black">{value}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/[0.03] rounded-full p-1 mb-6 w-fit">
        {[["overview","Overview"],["members","Members"],["courses","Courses"],["report","Report"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-white text-[#0E0F12]" : "text-gray-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === "overview" && (
        <div className="space-y-4">
          {org.courses_detail?.length === 0 ? (
            <div className="text-center py-12 border border-white/[0.06] rounded-2xl">
              <BookOpen className="w-9 h-9 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No courses assigned yet. Go to the Courses tab to assign some.</p>
            </div>
          ) : (
            org.courses_detail?.map((c) => (
              <div key={c.course_id} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">{c.title}</h3>
                  <div className="flex items-center gap-3">
                    {c.due_date && <span className="text-xs text-gray-500">Due {fmtDate(c.due_date)}</span>}
                    <span className="text-sm font-bold text-[#CFFF50]">{c.avg_progress}%</span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-2">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#7C5CFF] to-[#CFFF50] transition-all" style={{ width: `${c.avg_progress}%` }} />
                </div>
                <p className="text-xs text-gray-500">{c.completed_count} of {c.learner_count} learners completed</p>
              </div>
            ))
          )}
        </div>
      )}

      {/* Members tab */}
      {tab === "members" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-400">{(org.members || []).length} member{(org.members || []).length !== 1 ? "s" : ""}</p>
            <button onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors">
              <Upload size={14} /> Import CSV
            </button>
          </div>
          {(org.members || []).length === 0 ? (
            <div className="text-center py-12 border border-white/[0.06] rounded-2xl">
              <Users className="w-9 h-9 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No members yet. Use Import CSV to add learners and managers.</p>
            </div>
          ) : (
            <div className="border border-white/[0.08] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.08] text-xs text-gray-500 uppercase tracking-wider">
                    {["Name","Email","Role","Enrolled","Avg Progress"].map((h) => (
                      <th key={h} className="text-left px-5 py-3 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {org.members.map((m) => (
                    <tr key={m.user_id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-3 font-semibold">{m.name}</td>
                      <td className="px-5 py-3 text-gray-400 text-xs">{m.email}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${m.role === "manager" ? "bg-[#7C5CFF]/10 text-[#7C5CFF]" : "bg-white/5 text-gray-400"}`}>
                          {m.role}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-400">{m.enrolled_count}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full bg-[#CFFF50]" style={{ width: `${m.avg_progress}%` }} />
                          </div>
                          <span className="text-xs text-gray-400">{m.avg_progress}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Courses tab */}
      {tab === "courses" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-400">{(org.assigned_courses || []).length} assigned course{(org.assigned_courses || []).length !== 1 ? "s" : ""}</p>
            <button onClick={() => setShowAssign(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-colors">
              <Plus size={14} /> Assign Courses
            </button>
          </div>
          {org.courses_detail?.length === 0 ? (
            <div className="text-center py-12 border border-white/[0.06] rounded-2xl">
              <BookOpen className="w-9 h-9 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No courses assigned. Click "Assign Courses" to bulk-enrol all learners.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {org.courses_detail.map((c) => (
                <div key={c.course_id} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
                  {c.thumbnail && <div className="aspect-video overflow-hidden"><img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" /></div>}
                  <div className="p-4">
                    <h3 className="font-semibold text-sm mb-2 line-clamp-2">{c.title}</h3>
                    {c.due_date && <p className="text-xs text-gray-500 mb-2">Due {fmtDate(c.due_date)}</p>}
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-gray-500">{c.completed_count}/{c.learner_count} completed</span>
                      <span className="text-[#CFFF50] font-bold">{c.avg_progress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full bg-[#CFFF50]" style={{ width: `${c.avg_progress}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report tab */}
      {tab === "report" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-400">Completion report — all learners × assigned courses</p>
            <button onClick={exportCsv}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors">
              <Download size={14} /> Export CSV
            </button>
          </div>
          {reportLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading report…</div>
          ) : !report || report.length === 0 ? (
            <div className="text-center py-12 border border-white/[0.06] rounded-2xl">
              <BarChart3 className="w-9 h-9 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No data yet. Add learners and assign courses to see the report.</p>
            </div>
          ) : (
            <div className="border border-white/[0.08] rounded-2xl overflow-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr className="border-b border-white/[0.08] text-xs text-gray-500 uppercase tracking-wider bg-white/[0.02]">
                    <th className="text-left px-5 py-3 font-semibold sticky left-0 bg-[#0E0F12]">Learner</th>
                    {report[0]?.courses.map((c) => (
                      <th key={c.course_id} className="text-left px-5 py-3 font-semibold">{c.course_title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {report.map((row) => (
                    <tr key={row.user_id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-3 sticky left-0 bg-[#0E0F12]">
                        <div className="font-semibold">{row.name}</div>
                        <div className="text-xs text-gray-500">{row.email}</div>
                      </td>
                      {row.courses.map((c) => (
                        <td key={c.course_id} className="px-5 py-3">
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold self-start ${STATUS_STYLE[c.status] || "bg-white/5 text-gray-400"}`}>
                              {c.status.replace("_", " ")}
                            </span>
                            <span className="text-xs text-gray-400">{c.progress}%</span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CSV import modal */}
      {showImport && <CSVImportModal orgId={orgId} onClose={() => { setShowImport(false); load(); }} />}

      {/* Assign courses modal */}
      {showAssign && (
        <AssignCoursesModal
          orgId={orgId} courses={courses} assignedIds={assignedIds}
          onClose={() => { setShowAssign(false); load(); }}
        />
      )}
    </AppLayout>
  );
}

// ─── CSV import modal ────────────────────────────────────────────────────────

function CSVImportModal({ orgId, onClose }) {
  const [csv,      setCsv]      = useState("name,email,role\n");
  const [preview,  setPreview]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const fileRef = useRef(null);

  const parse = (text) => {
    const lines = text.trim().split("\n").filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      const cols = line.split(",").map((c) => c.trim());
      return Object.fromEntries(headers.map((h, i) => [h, cols[i] || ""]));
    }).filter((row) => row.email);
  };

  const onTextChange = (val) => { setCsv(val); setPreview(parse(val)); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onTextChange(ev.target.result);
    reader.readAsText(file);
  };

  const doImport = async () => {
    if (!preview.length) return toast.error("No valid rows to import");
    setSaving(true);
    try {
      const res = await api.post(`/admin/organisations/${orgId}/import-users`, { users: preview });
      toast.success(`Imported: ${res.added} added, ${res.created} new accounts created`);
      onClose();
    } catch { toast.error("Import failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#111318] border border-white/10 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
          <h3 className="font-bold text-base">Import Users via CSV</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-white/[0.03] rounded-xl p-3 text-xs text-gray-500 font-mono">
            name,email,role<br />Jamie Smith,jamie@acme.com,learner<br />Sarah Jones,sarah@acme.com,manager
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs text-gray-400">Paste CSV or</label>
              <button onClick={() => fileRef.current?.click()} className="text-xs text-[#CFFF50] hover:underline">upload file</button>
              <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={onFile} />
            </div>
            <textarea rows={6} className="w-full bg-[#0E0F12] border border-white/10 rounded-xl p-3 text-xs text-white font-mono focus:outline-none focus:border-[#CFFF50]/40 resize-none"
              value={csv} onChange={(e) => onTextChange(e.target.value)} />
          </div>
          {preview.length > 0 && (
            <div className="text-xs text-gray-400 bg-white/[0.03] rounded-xl p-3">
              <p className="font-semibold text-white mb-1">{preview.length} user{preview.length !== 1 ? "s" : ""} to import</p>
              {preview.slice(0, 3).map((u, i) => <p key={i}>{u.name} &lt;{u.email}&gt; — {u.role}</p>)}
              {preview.length > 3 && <p className="text-gray-600">+{preview.length - 3} more…</p>}
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5">Cancel</button>
          <button onClick={doImport} disabled={saving || !preview.length}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Import {preview.length > 0 ? preview.length : ""} users
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── assign courses modal ────────────────────────────────────────────────────

function AssignCoursesModal({ orgId, courses, assignedIds, onClose }) {
  const [selected, setSelected] = useState(new Set());
  const [dueDate,  setDueDate]  = useState("");
  const [saving,   setSaving]   = useState(false);

  const toggle = (id) => setSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  const assign = async () => {
    if (!selected.size) return toast.error("Select at least one course");
    setSaving(true);
    try {
      const payload = [...selected].map((id) => ({ course_id: id, due_date: dueDate || null }));
      const res = await api.post(`/admin/organisations/${orgId}/assign-courses`, { courses: payload });
      toast.success(`${res.courses_added} course${res.courses_added !== 1 ? "s" : ""} assigned · ${res.new_enrollments} new enrollments`);
      onClose();
    } catch { toast.error("Assignment failed"); }
    finally { setSaving(false); }
  };

  const available = courses.filter((c) => !assignedIds.has(c.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" onClick={onClose}>
      <div className="w-full max-w-md bg-[#111318] border border-white/10 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
          <h3 className="font-bold text-base">Assign Courses</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {available.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">All courses are already assigned to this organisation.</p>
          ) : (
            <>
              <div>
                <label className="text-xs text-gray-400 block mb-1.5">Optional due date for all selected courses</label>
                <input type="date" className="w-full bg-[#0E0F12] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#CFFF50]/40" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                {available.map((c) => (
                  <button key={c.id} onClick={() => toggle(c.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${selected.has(c.id) ? "border-[#CFFF50] bg-[#CFFF50]/5" : "border-white/[0.08] hover:border-white/20"}`}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${selected.has(c.id) ? "bg-[#CFFF50] border-[#CFFF50]" : "border-white/20"}`}>
                      {selected.has(c.id) && <Check size={10} className="text-[#0E0F12]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{c.title}</p>
                      <p className="text-xs text-gray-500">{c.category}</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5">Cancel</button>
          <button onClick={assign} disabled={saving || !selected.size}
            className="px-5 py-2 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover disabled:opacity-50">
            {saving ? "Assigning…" : `Assign ${selected.size || ""} course${selected.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
